import NextLink from 'next/link'

import { Masonry } from '@mui/lab'
import { Box, Grid, Stack } from '@mui/material'

import ColumnLinks from '@/components/MegaMenu/ColumnLinks'
import { SubMenuItemTitleLink, TitleWrapper } from '@/components/MegaMenu/MegaMenu.styled'
import { getTarget } from '@/helpers/getTarget'
import { getHighlightedLinkStyle } from '@/helpers/linkColors'
import { useHeaderContext } from '@/hooks/useHeaderContext'

import type { FC } from 'react'
import type { NavigationNode, TopNavigationNode } from '@/interfaces/NavigationNode'

interface MenuContentColumnsProps {
  node: TopNavigationNode
}

export const MenuContentColumns: FC<MenuContentColumnsProps> = ({ node }) => (
  <Box maxWidth={{ lg: 'xl', md: 'md' }} sx={{ margin: 'auto', p: 3 }}>
    <Masonry spacing={3} columns={{ md: 3, lg: 4 }}>
      {node.children.map((column, colIndex) => (
        <Grid item key={column.id}>
          <Stack direction="column">
            <TitleWrapper>
              <ColumnTitle column={column} colIndex={colIndex} parentId={node.id} />
            </TitleWrapper>
            {column.children.length > 0 && (
              <ColumnLinks links={column.children} colIndex={colIndex} parentId={node.id} />
            )}
          </Stack>
        </Grid>
      ))}
    </Masonry>
  </Box>
)

interface ColumnTitleProps {
  column: NavigationNode
  colIndex: number
  parentId: string
}

const ColumnTitle: FC<ColumnTitleProps> = ({ column, colIndex, parentId }) => {
  const { toggleIsMegaMenuOpen } = useHeaderContext()
  const titleProps = {
    variant: 'body2',
    fontSize: 16,
    sx: { color: 'secondary.main' },
    style: getHighlightedLinkStyle(column),
  } as const

  if (!column.href) {
    return (
      <SubMenuItemTitleLink component="span" underline="none" {...titleProps}>
        {column.title}
      </SubMenuItemTitleLink>
    )
  }

  return (
    <SubMenuItemTitleLink
      component={NextLink}
      href={column.href}
      {...getTarget(column.openInNewTab)}
      data-parent-id={parentId}
      data-col-index={colIndex}
      data-row-index={0}
      underline="hover"
      onClick={toggleIsMegaMenuOpen(false, null, true)}
      {...titleProps}
    >
      {column.title}
    </SubMenuItemTitleLink>
  )
}
